import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { useSession } from '../session/SessionProvider'
import { Busy, ErrorBanner, Field, Notice, PasswordInput, TextInput } from '../ui/controls'
import { Shell } from '../ui/Layout'

export function SignUp() {
  const { t } = useI18n()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  return (
    <Shell>
      <h1>{t('auth.signUp')}</h1>
      <p className="lede">{t('app.tagline')}</p>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          setBusy(true)

          api.auth
            .register(email, password)
            // Straight to the page that tells them to check their inbox. The address is carried
            // in the URL so "resend" works without asking for it again (G19-2).
            .then(() => navigate(`/verify?email=${encodeURIComponent(email)}`))
            .catch(setError)
            .finally(() => setBusy(false))
        }}
      >
        <ErrorBanner error={error} />

        <Field label={t('auth.email')} required>
          <TextInput value={email} onChange={setEmail} type="email" autoComplete="email" required />
        </Field>

        <Field label={t('auth.password')} hint={t('auth.passwordHint')} required>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
        </Field>

        <button type="submit" className="primary" disabled={busy}>
          {busy ? t('common.saving') : t('auth.signUp')}
        </button>

        <Link to="/signin">{t('auth.haveAccount')}</Link>
      </form>
    </Shell>
  )
}

export function SignIn() {
  const { t } = useI18n()
  const { refresh } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [code, setCode] = useState('')
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // The password was right and a second factor is still owed (安全架构 §21). No session exists
  // yet — the API parked the identity in a short-lived cookie and issued nothing else — so this
  // is a second step, not a second page.
  if (twoFactorRequired) {
    return (
      <Shell>
        <h1>{t('twofactor.title')}</h1>

        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            setBusy(true)

            api.auth
              .twoFactor(code)
              .then(() => refresh())
              .catch(setError)
              .finally(() => setBusy(false))
          }}
        >
          <ErrorBanner error={error} />

          <Field label={t('twofactor.prompt')} required>
            <TextInput
              value={code}
              onChange={setCode}
              autoComplete="one-time-code"
              maxLength={16}
              required
            />
          </Field>

          <button type="submit" className="primary" disabled={busy}>
            {busy ? t('common.loading') : t('twofactor.verify')}
          </button>
        </form>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1>{t('auth.signIn')}</h1>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          setBusy(true)

          api.auth
            .login(email, password, rememberMe)
            .then((needsCode) => {
              if (needsCode) {
                setTwoFactorRequired(true)
                return undefined
              }

              // The session provider decides where to go; it is the one that knows.
              return refresh()
            })
            .catch(setError)
            .finally(() => setBusy(false))
        }}
      >
        <ErrorBanner error={error} />

        <Field label={t('auth.email')} required>
          <TextInput value={email} onChange={setEmail} type="email" autoComplete="email" required />
        </Field>

        <Field label={t('auth.password')} required>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />
        </Field>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
          />
          {t('auth.rememberMe')}
        </label>

        <button type="submit" className="primary" disabled={busy}>
          {busy ? t('common.loading') : t('auth.signIn')}
        </button>

        <div className="row-links">
          <Link to="/forgot">{t('auth.forgot')}</Link>
          <Link to="/signup">{t('auth.noAccount')}</Link>
        </div>
      </form>
    </Shell>
  )
}

/**
 * Two jobs in one screen: confirming a link, and telling someone how to get another.
 *
 * **The resend button is a hard requirement (G19-2).** Verification email failures are recorded
 * in an Error log and nowhere else — they are not queued, not retried, and nobody is watching.
 * This button is the only recovery a user has, which is why it is on the page rather than behind
 * a support address.
 */
export function VerifyEmail() {
  const { t } = useI18n()
  const [params] = useSearchParams()

  const userId = params.get('userId')
  const token = params.get('token')
  const emailFromUrl = params.get('email') ?? ''

  const [email, setEmail] = useState(emailFromUrl)
  const [state, setState] = useState<'idle' | 'confirming' | 'confirmed'>(
    userId && token ? 'confirming' : 'idle',
  )
  const [resent, setResent] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (!userId || !token) {
      return
    }

    api.auth
      .verifyEmail(userId, token)
      .then(() => setState('confirmed'))
      .catch((cause) => {
        setError(cause)
        setState('idle')
      })
  }, [userId, token])

  if (state === 'confirming') {
    return (
      <Shell>
        <Busy label={t('verify.checking')} />
      </Shell>
    )
  }

  if (state === 'confirmed') {
    return (
      <Shell>
        <h1>{t('verify.title')}</h1>
        <Notice>{t('verify.done')}</Notice>
        <Link className="primary as-link" to="/signin">
          {t('verify.goSignIn')}
        </Link>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1>{t('verify.title')}</h1>

      <ErrorBanner error={error} />

      {emailFromUrl ? (
        <p>
          {t('verify.sentTo')} <strong>{emailFromUrl}</strong>
        </p>
      ) : null}

      <p className="lede">{t('verify.whyRequired')}</p>

      {resent ? <Notice>{t('verify.resent')}</Notice> : null}

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)

          api.auth
            .resendVerification(email)
            .then(() => setResent(true))
            .catch(setError)
        }}
      >
        <Field label={t('verify.notArrived')} required>
          <TextInput value={email} onChange={setEmail} type="email" required />
        </Field>

        <button type="submit" className="primary">
          {t('verify.resend')}
        </button>
      </form>
    </Shell>
  )
}

export function ForgotPassword() {
  const { t } = useI18n()

  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<unknown>(null)

  return (
    <Shell>
      <h1>{t('forgot.title')}</h1>
      <p className="lede">{t('forgot.explain')}</p>

      <ErrorBanner error={error} />
      {sent ? <Notice>{t('forgot.sent')}</Notice> : null}

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)

          api.auth
            .forgotPassword(email)
            // The API answers the same way whether or not the address exists, and so does this
            // screen: anything else would turn the form into a way to test addresses.
            .then(() => setSent(true))
            .catch(setError)
        }}
      >
        <Field label={t('auth.email')} required>
          <TextInput value={email} onChange={setEmail} type="email" required />
        </Field>

        <button type="submit" className="primary">
          {t('common.next')}
        </button>

        <Link to="/signin">{t('auth.signIn')}</Link>
      </form>
    </Shell>
  )
}

export function ResetPassword() {
  const { t } = useI18n()
  const [params] = useSearchParams()

  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const token = params.get('token') ?? ''

  if (done) {
    return (
      <Shell>
        <h1>{t('reset.title')}</h1>
        <Notice>{t('reset.done')}</Notice>
        <Link className="primary as-link" to="/signin">
          {t('auth.signIn')}
        </Link>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1>{t('reset.title')}</h1>

      <ErrorBanner error={error} />

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)

          api.auth
            .resetPassword(email, token, password)
            .then(() => setDone(true))
            .catch(setError)
        }}
      >
        <Field label={t('auth.email')} required>
          <TextInput value={email} onChange={setEmail} type="email" required />
        </Field>

        <Field label={t('reset.newPassword')} hint={t('auth.passwordHint')} required>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
        </Field>

        <button type="submit" className="primary">
          {t('common.save')}
        </button>
      </form>
    </Shell>
  )
}
